import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Modal, Button, Radio, Stack } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { RowCheckbox } from "../RowCheckbox";
import {
	getAccessorKey,
	getAllColumns,
	buildColumnSelection,
	generateCSVContent,
	downloadCSV,
} from "./csvUtils";

/*
	Reusable CSV export modal.

	Props:
		allData:               Array - the full unfiltered dataset
		filteredData:          Array - data after all active filters/tabs
		columns:               Array - column definitions: [{ Header: string, accessor: string }]
		extraColumns:          Array - optional extra columns with computed values:
		                       [{ Header: string, accessor: string | (row) => value }]
		csvColumnRenderers:       Object - optional map of accessor -> (value, row) => formattedString
		                       for custom formatting in CSV output (e.g. bytes to human-readable)
		columnVisibility:      Object - map of accessor -> boolean from Redux column visibility state
		fileName:              string - filename without extension (default: "export")
*/

const ExportToCSV = forwardRef(function ExportToCSV(props, ref) {
	const [isOpen, setIsOpen] = useState(false);
	const [dataScope, setDataScope] = useState("filtered");
	const [selectedColumns, setSelectedColumns] = useState({});

	useImperativeHandle(ref, () => ({
		open() {
			setIsOpen(true);
			setDataScope("filtered");
			setSelectedColumns(buildUserColumnSelection());
		},
	}));

	function buildUserColumnSelection() {
		const allCols = getAllColumns(props.columns, props.extraColumns);
		return buildColumnSelection(allCols, props.columnVisibility);
	}

	function toggleColumn(accessorKey) {
		setSelectedColumns((prev) => ({
			...prev,
			[accessorKey]: !prev[accessorKey],
		}));
	}

	function selectAllColumns() {
		const selected = {};
		getAllColumns(props.columns, props.extraColumns).forEach((col) => {
			selected[getAccessorKey(col)] = true;
		});
		setSelectedColumns(selected);
	}

	function resetToUserColumns() {
		setSelectedColumns(buildUserColumnSelection());
	}

	function deselectAllColumns() {
		const selected = {};
		getAllColumns(props.columns, props.extraColumns).forEach((col) => {
			selected[getAccessorKey(col)] = false;
		});
		setSelectedColumns(selected);
	}

	function generateAndDownload() {
		const data = dataScope === "all" ? props.allData : props.filteredData;

		const allCols = getAllColumns(props.columns, props.extraColumns);

		const csvContent = generateCSVContent({
			data,
			allColumns: allCols,
			selectedColumns,
			csvColumnRenderers: props.csvColumnRenderers,
		});

		if (!csvContent) return;

		downloadCSV(csvContent, props.fileName);
		setIsOpen(false);
	}

	const allCols = getAllColumns(props.columns, props.extraColumns);
	const allDataCount = (props.allData || []).length;
	const filteredDataCount = (props.filteredData || []).length;
	const selectedCount = Object.values(selectedColumns).filter(Boolean).length;

	return (
		<Modal
			opened={isOpen}
			onClose={() => setIsOpen(false)}
			title="Export to CSV"
			size="md"
		>
			<Stack gap="md">
				{/* Data scope */}
				<div>
					<div style={{ fontWeight: "bold", marginBottom: 8 }}>
						Data to export
					</div>
					<Radio.Group value={dataScope} onChange={setDataScope}>
						<Stack gap="xs">
							<Radio
								value="filtered"
								label={`Filtered view (${filteredDataCount} rows)`}
							/>
							<Radio
								value="all"
								label={`All data (${allDataCount} rows)`}
							/>
						</Stack>
					</Radio.Group>
				</div>

				{/* Column selection */}
				<div>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 8,
						}}
					>
						<span style={{ fontWeight: "bold" }}>
							Columns ({selectedCount} of {allCols.length})
						</span>
						<span>
							<span
								onClick={selectAllColumns}
								style={{
									cursor: "pointer",
									color: "var(--lxw-main-500)",
									marginRight: 12,
								}}
							>
								All
							</span>
							<span
								onClick={deselectAllColumns}
								style={{
									cursor: "pointer",
									color: "var(--lxw-main-500)",
									marginRight: 12,
								}}
							>
								None
							</span>
							<span
								onClick={resetToUserColumns}
								style={{
									cursor: "pointer",
									color: "var(--lxw-main-500)",
								}}
							>
								My Columns
							</span>
						</span>
					</div>
					<div
						style={{
							maxHeight: 600,
							overflowY: "auto",
							border: "1px solid #ddd",
							borderRadius: 4,
							padding: "4px 0",
						}}
					>
						{allCols.map((col) => {
							const key = getAccessorKey(col);
							const selected = selectedColumns[key];
							return (
								<RowCheckbox
									key={key}
									checked={!!selected}
									onChange={() => toggleColumn(key)}
									label={col.csvColumnName || col.Header}
									styles={{
										root: {
											padding: "6px 12px",
											cursor: "pointer",
											userSelect: "none",
										},
										label: { cursor: "pointer" },
									}}
								/>
							);
						})}
					</div>
				</div>

				{/* Actions */}
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						alignItems: "center",
						gap: 8,
					}}
				>
					{selectedCount === 0 ? (
						<span
							style={{
								color: "var(--lw-darker-red)",
								marginRight: "auto",
								fontSize: 13,
							}}
						>
							Select at least one column to export
						</span>
					) : null}
					<Button variant="default" onClick={() => setIsOpen(false)}>
						Cancel
					</Button>
					<Button
						onClick={generateAndDownload}
						disabled={selectedCount === 0}
						leftSection={<IconDownload size={16} />}
					>
						Export CSV
					</Button>
				</div>
			</Stack>
		</Modal>
	);
});

export default ExportToCSV;
